import React from 'react';
import { Play, Square, Pause, RotateCcw, Search, RefreshCw, ArrowDownToLine, ArrowUpFromLine, ArrowRightFromLine } from 'lucide-react';

const FILE_OPS = [
  { title:'New File', icon: <svg viewBox="0 0 16 16" fill="none" className="w-4 h-4"><rect x="3" y="1" width="9" height="13" rx="1" stroke="#CCC" strokeWidth="1.2"/><path d="M9 1v4h3" stroke="#CCC" strokeWidth="1.2"/></svg> },
  { title:'Open',     icon: <svg viewBox="0 0 16 16" fill="none" className="w-4 h-4"><path d="M2 5h4l1.5 2H14v7H2V5Z" stroke="#CCC" strokeWidth="1.2"/></svg> },
  { title:'Save',     icon: <svg viewBox="0 0 16 16" fill="none" className="w-4 h-4"><rect x="2" y="2" width="12" height="12" rx="1" stroke="#CCC" strokeWidth="1.2"/><rect x="5" y="2" width="5" height="4" fill="#CCC" opacity="0.5"/><rect x="3" y="9" width="10" height="4" fill="#CCC" opacity="0.35"/></svg> },
];

/* Small icon button used for debug toolbar controls */
function ToolBtn({ title, onClick, children, disabled, className = '' }) {
  return (
    <button
      title={title}
      onClick={disabled ? undefined : onClick}
      className={`w-[24px] h-[22px] flex items-center justify-center rounded ${disabled ? 'opacity-40 cursor-not-allowed' : 'hover:bg-white/10 cursor-pointer'} ${className}`}
    >
      {children}
    </button>
  );
}

export default function Toolbar({ step, runBtnRef, onBuild, onStop, onRestart, projectName = 'HelloWorld' }) {
  const isBuilding  = step === 3;
  const isDebugging = step === 4;
  const isBusy      = isBuilding || isDebugging;

  return (
    <div className="flex items-center h-[28px] bg-[#2d2d30] border-b border-[#3f3f41] px-2 gap-1 flex-shrink-0">
      {/* File ops */}
      {FILE_OPS.map(({ title, icon }) => (
        <button key={title} title={title} className="w-[24px] h-[22px] flex items-center justify-center rounded hover:bg-white/10 text-[#ccc]">
          {icon}
        </button>
      ))}

      <div className="w-px h-[18px] bg-[#555] mx-1"/>

      {/* Undo / Redo */}
      <button title="Undo" className="w-[24px] h-[22px] flex items-center justify-center rounded hover:bg-white/10 text-[13px]">↩</button>
      <button title="Redo" className="w-[24px] h-[22px] flex items-center justify-center rounded hover:bg-white/10 text-[13px]">↪</button>

      <div className="w-px h-[18px] bg-[#555] mx-1"/>

      {/* Config + Platform — disabled while building / debugging */}
      <select
        disabled={isBusy}
        className={`bg-[#3c3c3c] border border-[#555] text-[#ccc] text-[11px] h-[20px] px-1 rounded-sm outline-none ${isBusy ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
      >
        <option>Debug</option><option>Release</option>
      </select>
      <select
        disabled={isBusy}
        className={`bg-[#3c3c3c] border border-[#555] text-[#ccc] text-[11px] h-[20px] px-1 rounded-sm outline-none ml-1 w-[90px] ${isBusy ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
      >
        <option>Any CPU</option><option>x64</option>
      </select>

      <div className="w-px h-[18px] bg-[#555] mx-1"/>

      {/* ── Run / Stop button ── */}
      {isBusy ? (
        /* Building or Debugging → show Stop (red square) — clickable */
        <button
          ref={runBtnRef}
          onClick={onStop}
          title="Stop Debugging (Shift+F5)"
          className="flex items-center gap-1.5 px-3 h-[22px] rounded-sm text-[12px] text-[#ccc] border border-transparent hover:bg-white/10 hover:border-white/10 cursor-pointer transition-colors"
        >
          <Square size={13} className="text-red-500 fill-red-500"/>
          <span>{projectName}</span>
        </button>
      ) : (
        /* Ready → show Play (green triangle) */
        <button
          ref={runBtnRef}
          onClick={step === 2 ? onBuild : undefined}
          title="Start Debugging (F5)"
          className={[
            'flex items-center gap-1.5 px-3 h-[22px] rounded-sm text-[12px] text-[#ccc] border border-transparent transition-colors',
            step === 2 ? 'hover:bg-white/10 hover:border-white/10 cursor-pointer ring-1 ring-[#57A64A]/60' : '',
          ].join(' ')}
        >
          <Play size={13} className="text-[#57A64A] fill-[#57A64A]"/>
          <span>{projectName}</span>
        </button>
      )}

      {/* ── Debug controls — only visible when debugging (step 4) ── */}
      {isDebugging && (
        <>
          <ToolBtn title="Pause"><Pause size={13} className="text-[#ccc]"/></ToolBtn>
          <ToolBtn title="Restart (Ctrl+Shift+F5)" onClick={onRestart}>
            <RotateCcw size={13} className="text-[#57A64A]"/>
          </ToolBtn>

          <div className="w-px h-[18px] bg-[#555] mx-1"/>

          {/* Step controls */}
          <ToolBtn title="Step Over (F10)"><ArrowRightFromLine size={13} className="text-[#ccc]"/></ToolBtn>
          <ToolBtn title="Step Into (F11)"><ArrowDownToLine size={13} className="text-[#ccc]"/></ToolBtn>
          <ToolBtn title="Step Out (Shift+F11)"><ArrowUpFromLine size={13} className="text-[#ccc]"/></ToolBtn>
        </>
      )}

      <div className="w-px h-[18px] bg-[#555] mx-1"/>

      {/* Find */}
      <button title="Find" className="w-[24px] h-[22px] flex items-center justify-center rounded hover:bg-white/10">
        <Search size={14} className="text-[#ccc]"/>
      </button>

      {/* Restart simulation */}
      {step > 0 && (
        <button
          onClick={onRestart}
          title="Restart simulation"
          className="ml-auto flex items-center gap-1 px-2 h-[22px] rounded-sm text-[11px] text-[#999] hover:text-[#ccc] hover:bg-white/10"
        >
          <RefreshCw size={12}/> Restart
        </button>
      )}
    </div>
  );
}
